import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { current } from "immer";
import type { OfferDetailResponse } from "@codway-dev/shared";
import { OfferService } from "../services/offer.service";
import { isDeepEqual } from "../utils/deep-equal";
import {
  DEFAULT_ONE_TICK_EDITOR_STATE,
  offerToState,
  stateToCreateRequest,
  stateToUpdateRequest,
  type OneTickEditorState,
} from "./one-tick-editor.mapping";

// ─── Types ────────────────────────────────────────────────────────────────────

interface OneTickEditorStoreState {
  config: OneTickEditorState;
  offerId: string | null;
  originalConfig: OfferDetailResponse["config"] | null;
  // Captured at load/save time so the dirty check has a stable baseline
  // to compare against. Held by reference — Immer auto-freezes nested
  // objects in produced state, so subsequent edits draft a fresh copy
  // instead of mutating this snapshot.
  initialConfig: OneTickEditorState | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface OneTickEditorActions {
  initNew: () => void;
  loadFromBackend: (id: string) => Promise<void>;
  updateField: <K extends keyof OneTickEditorState>(
    key: K,
    value: OneTickEditorState[K]
  ) => void;
  save: () => Promise<string>;
  discardChanges: () => void;
  reset: () => void;
}

type OneTickEditorStore = OneTickEditorStoreState & OneTickEditorActions;

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectOneTickIsDirty = (state: OneTickEditorStore) => {
  if (state.initialConfig === null) return false;
  return !isDeepEqual(state.config, state.initialConfig);
};

// ─── Store ───────────────────────────────────────────────────────────────────

const INITIAL_STATE: OneTickEditorStoreState = {
  config: DEFAULT_ONE_TICK_EDITOR_STATE,
  offerId: null,
  originalConfig: null,
  initialConfig: null,
  isLoading: false,
  isSaving: false,
  error: null,
};

export const useOneTickEditorStore = create<OneTickEditorStore>()(
  devtools(
    immer((set, get) => ({
      ...INITIAL_STATE,

      initNew: () =>
        set((state) => {
          state.config = DEFAULT_ONE_TICK_EDITOR_STATE;
          state.offerId = null;
          state.originalConfig = null;
          state.initialConfig = DEFAULT_ONE_TICK_EDITOR_STATE;
          state.isLoading = false;
          state.isSaving = false;
          state.error = null;
        }),

      loadFromBackend: async (id) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const offer = await OfferService.get(id);
          const hydrated = offerToState(offer);
          set((state) => {
            state.config = hydrated;
            state.offerId = offer.id;
            state.originalConfig = offer.config;
            state.initialConfig = hydrated;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error
                ? err.message
                : "No se pudo cargar el upsell";
            state.isLoading = false;
          });
          throw err;
        }
      },

      updateField: (key, value) =>
        set((state) => {
          state.config[key] = value as never;
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
          state.error = null;
        });

        try {
          const s = get();

          let savedId: string;

          if (s.offerId === null) {
            const created = await OfferService.create(
              stateToCreateRequest(s.config)
            );
            savedId = created.id;
          } else {
            const updated = await OfferService.update(
              s.offerId,
              stateToUpdateRequest(s.config, s.originalConfig)
            );
            savedId = updated.id;
          }

          set((state) => {
            state.offerId = savedId;
            // `current()` strips the Immer draft proxy and returns a
            // plain frozen snapshot — the dirty check compares values,
            // so we need the unwrapped object, not the draft.
            state.initialConfig = current(state.config);
            state.isSaving = false;
          });

          return savedId;
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Error al guardar el upsell";
          set((state) => {
            state.isSaving = false;
            state.error = message;
          });
          throw new Error(message);
        }
      },

      discardChanges: () =>
        set((state) => {
          if (!state.initialConfig) return;
          state.config = state.initialConfig;
        }),

      reset: () =>
        set((state) => {
          Object.assign(state, INITIAL_STATE);
        }),
    })),
    { name: "OneTickEditorStore" }
  )
);
